import React from 'react';

import type { AlineSelection } from '@aline/common';
import { Select } from '@aline/common';
import { Point, Range } from 'slate';
import { AlineButton } from 'src/components';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { getLiveSpellcheckRange, isRangeLive } from 'src/editor/spellcheck-helpers';
import { useSpellcheckStateSelector } from 'src/editor/spellcheck-state-registry';
import type { SpellcheckLint } from 'src/editor/spellcheck-vars';
import {
  SuggestionPills,
  usePillStyles,
} from 'src/editor/toolbars/right-panel/spellcheck/SuggestionPills';
import { useSpellcheckActions } from 'src/editor/use-spellcheck-actions';
import { makeStyles } from 'tss-react/mui';

import { RangePopover } from './RangePopover';
import { useEditor, useRangeRects, useSelection } from '../hooks';

const useStyles = makeStyles()(theme => ({
  message: {
    color: theme.appColors.strongType,
    fontSize: 12,
    marginBottom: theme.spacing(0.75),
  },
  pills: { alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 6 },
  popover: {
    background: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 10,
    boxShadow: theme.appShadows.popper,
    maxWidth: 300,
    minWidth: 200,
    padding: theme.spacing(1.25),
    width: 'max-content',
  },
  row: { alignItems: 'center', display: 'flex', gap: 8, justifyContent: 'space-between' },
}));

export function SpellcheckPopover() {
  const { classes } = useStyles();
  const { classes: pillClasses } = usePillStyles();
  const editor = useEditor();
  const selection = useSelection();
  const lints = useSpellcheckStateSelector(state => state.lints);
  const isTyping = useSpellcheckStateSelector(state => state.typing);
  const { acceptLint, canReplace, ignoreLints } = useSpellcheckActions();

  const clickedPoint = useEditorTabStateSelector(state => state.click.lastMouseDownPoint);
  const isDraggingSelection = useIsDraggingSelection();
  const caretAnchor = getClickedCaret({ clickedPoint, isDraggingSelection, isTyping, selection });
  const activeLint: SpellcheckLint | null = caretAnchor
    ? (lints.find(lint => {
        const range = getLiveSpellcheckRange(lint);
        if (!range || !isRangeLive(editor, range)) return false;
        return Range.includes(range, caretAnchor);
      }) ?? null)
    : null;

  const rangeRects = useRangeRects(activeLint ? getLiveSpellcheckRange(activeLint) : null);

  if (!activeLint) return null;

  return (
    <RangePopover placement="bottom" rangeRects={rangeRects}>
      <div className={classes.popover} data-cy={Select.document.editor.spellcheckPopover.container}>
        <div className={classes.message}>{activeLint.message}</div>
        <div className={classes.row}>
          <div className={classes.pills}>
            <SuggestionPills canReplace={canReplace} lint={activeLint} onAccept={acceptLint} />
          </div>
          <AlineButton
            buttonClassName={pillClasses.pill}
            color="primary"
            size="small"
            variant="text"
            onClick={() => ignoreLints([activeLint])}
          >
            Ignore
          </AlineButton>
        </div>
      </div>
    </RangePopover>
  );
}

function getClickedCaret({
  clickedPoint,
  isDraggingSelection,
  isTyping,
  selection,
}: {
  clickedPoint: Point | null;
  isDraggingSelection: boolean;
  isTyping: boolean;
  selection: AlineSelection | null;
}): Point | undefined {
  if (
    isDraggingSelection ||
    isTyping ||
    !selection ||
    !clickedPoint ||
    Range.isExpanded(selection) ||
    !Point.equals(selection.anchor, clickedPoint)
  )
    return undefined;

  return selection.anchor;
}

/**
 * `mouseDownClick` is cleared on mouse up, so a selection is being dragged out whenever one is
 * registered. Without this the popover opens on mouse down and closes again the moment the drag
 * expands the selection, which the reader sees as a flash.
 */
function useIsDraggingSelection() {
  return Boolean(useEditorTabStateSelector(state => state.click.mouseDownClick));
}
